import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChatAttachment } from "@/components/ChatAttachment";
import { uploadFile, CHAT_MAX_BYTES } from "@/lib/chatUpload";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const MAX = 5;
const IMG_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";
const PHASES = [["before", "صور الاستلام"], ["after", "صور بعد الإصلاح"]];

const PhaseSection = ({ requestId, phase, label, items, canEdit, reload }) => {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - items.length);
    e.target.value = "";
    for (const file of files) {
      if (file.size > CHAT_MAX_BYTES) { toast.error(`${file.name}: أكبر من 25 ميجابايت`); continue; }
      setPct(0);
      try { await uploadFile(`/work-photos/${requestId}/${phase}/init`, file, setPct); }
      catch (ex) { toast.error(`${file.name}: ${formatApiError(ex.response?.data?.detail) || "تعذّر الرفع"}`); }
    }
    setPct(null);
    reload();
  };
  const remove = async (id) => {
    try { await api.delete(`/work-photos/file/${id}`); reload(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div data-testid={`work-photos-${phase}`}>
      <div className="text-sm font-semibold mb-2">{label} {canEdit && <span className="text-xs text-muted-foreground">({items.length}/{MAX})</span>}</div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {items.map((a) => (
          <div key={a.file_id} className="relative rounded-lg border border-border overflow-hidden" data-testid={`work-photo-${a.file_id}`}>
            <ChatAttachment att={a} compact />
            {canEdit && (
              <button type="button" onClick={() => remove(a.file_id)} className="absolute top-1 start-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center" aria-label="حذف" data-testid={`work-photo-remove-${a.file_id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {!canEdit && items.length === 0 && <div className="text-xs text-muted-foreground col-span-full">لا توجد صور</div>}
      </div>
      {canEdit && items.length < MAX && (
        <>
          <input ref={ref} type="file" multiple accept={IMG_ACCEPT} className="hidden" onChange={pick} data-testid={`work-photos-${phase}-input`} />
          <Button type="button" size="sm" variant="outline" className="mt-2 rounded-full gap-2" disabled={pct !== null} onClick={() => ref.current?.click()} data-testid={`work-photos-${phase}-add`}>
            {pct !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> {pct}%</> : <><Camera className="w-4 h-4" /> إضافة صور</>}
          </Button>
        </>
      )}
    </div>
  );
};

export const WorkPhotos = ({ requestId, canEdit = false, onLoaded }) => {
  const [data, setData] = useState({ before: [], after: [] });
  const load = () => api.get(`/work-photos/${requestId}`).then((r) => { setData(r.data); onLoaded && onLoaded(r.data); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [requestId]);
  if (!canEdit && !data.before.length && !data.after.length) return null;
  return (
    <div className="space-y-4" data-testid={`work-photos-${requestId}`}>
      {PHASES.map(([k, l]) => <PhaseSection key={k} requestId={requestId} phase={k} label={l} items={data[k]} canEdit={canEdit} reload={load} />)}
    </div>
  );
};
